/**
 * negative-scanner / convert.js — the film maths. Pure functions, no DOM, so
 * the same code runs the live preview, the full-resolution export and the
 * node tests (tests/js/negative-scanner-convert.test.mjs).
 *
 * WHY NOT "INVERT"
 * ────────────────
 * A colour negative is not a picture with its colours flipped. The film base
 * carries an orange mask (it corrects the dyes' unwanted absorptions at print
 * time), each dye layer has its own density range, and the camera that
 * photographs the negative adds its own white balance and tone. Plain
 * `255 - v` — what every "invert image" site does — gives a blue-cyan picture
 * with milky blacks. The steps below are the ones an enlarger and a lab do:
 *
 *   1. undo the camera's sRGB encoding → linear transmittance of the film;
 *   2. per channel, density above the film base: d = log10(base / t);
 *   3. density is film gamma × log exposure, so scene light ∝ 10^(d/γ)
 *      = (base / t)^(1/γ) — the orange mask divides out here, per channel;
 *   4. black point and white point per channel (the thinnest and densest
 *      parts of the frame, or the base itself when it was sampled) — this is
 *      the white balance and the black level at once;
 *   5. the viewer's adjustments, then sRGB encoding for the screen.
 *
 * Everything from 1 to 5 depends on ONE input channel value only, so the whole
 * chain collapses into a 256-entry lookup table per channel. The live preview
 * (WebGL), the still editor and the export all apply that same table — what
 * the viewfinder shows is, byte for byte, what the saved file contains.
 * Saturation is the one step that mixes channels; it is applied per pixel
 * after the table, identically everywhere.
 */

export const MODES = ['color', 'bw', 'slide'];

/** Straight-line gamma of the film's characteristic curve. C-41 colour
 *  negative films sit near 0.6 (Kodak/Fuji datasheets: 0.55–0.65), pictorial
 *  B&W developed to a normal contrast index near 0.6–0.65. A slide is a
 *  positive: it is corrected, never inverted, so it has no gamma here. */
export const FILM_GAMMA = { color: 0.6, bw: 0.62, slide: 1 };

/** Share of the frame clipped off at each end when the levels are automatic:
 *  enough to ignore a speck of dust or a hot pixel, too little to eat detail. */
export const AUTO_CLIP = { lo: 0.002, hi: 0.998 };

const clamp01 = (x) => (x < 0 ? 0 : x > 1 ? 1 : x);

/** sRGB decode of an 8-bit value (float 0..255 allowed) → linear 0..1. */
export function dec(v) {
    const c = v / 255;
    return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

/** sRGB encode, linear 0..1 → 0..1. */
export function enc(l) {
    l = clamp01(l);
    return l <= 0.0031308 ? 12.92 * l : 1.055 * Math.pow(l, 1 / 2.4) - 0.055;
}

/** Luma of an 8-bit RGB triple, integer 0..255 (Rec.709 weights on the
 *  encoded values, 54/183/19 of 256). The WebGL shader uses the same weights. */
export function luma8(r, g, b) {
    return (54 * r + 183 * g + 19 * b + 128) >> 8;
}

/** Is point (x, y) inside the convex quad q (4 points, either winding)? */
export function inQuad(q, x, y) {
    let s = 0;
    for (let i = 0; i < 4; i++) {
        const a = q[i], b = q[(i + 1) & 3];
        const c = (b.x - a.x) * (y - a.y) - (b.y - a.y) * (x - a.x);
        if (c === 0) continue;
        const sg = c > 0 ? 1 : -1;
        if (s === 0) s = sg; else if (sg !== s) return false;
    }
    return true;
}

/** Pull a quad towards its centre by `f` of the way (0.02 = 2 %). */
export function insetQuad(q, f) {
    const cx = (q[0].x + q[1].x + q[2].x + q[3].x) / 4;
    const cy = (q[0].y + q[1].y + q[2].y + q[3].y) / 4;
    return q.map((p) => ({ x: p.x + (cx - p.x) * f, y: p.y + (cy - p.y) * f }));
}

/** Histogram resolution: four bins per 8-bit level, so block-averaged
 *  (fractional) values keep their precision. */
export const HBINS = 1024;
const HS = HBINS / 256;

/**
 * Per-channel histograms of an RGBA buffer (integer or fractional values).
 *   opts.quad   — only pixels inside this quad count;
 *   opts.step   — sample every n-th pixel in both directions (live preview);
 *   opts.box    — average k×k blocks first (see below);
 *   opts.clipAt — pixels with any channel at or above this are left out of
 *                 the histograms (counted in `clipped`). For a negative such a
 *                 pixel is bare backlight or a clipped base: it would drag the
 *                 black point down and milk the shadows.
 */
export function histograms(data, w, h, opts = {}) {
    if ((opts.box | 0) > 1) {
        // Average k×k blocks first. The black and white points sit in the
        // tails, and on the dense end of a negative one level of sensor noise
        // is several percent of the light: measured on the synthetic set,
        // per-pixel stats put the white point on the noise tail and dimmed
        // every highlight by ~8 levels. Averaging keeps the fraction (the
        // buffer is float) — rounding it back to 8 bits undid half the gain.
        const k = opts.box | 0, bw = Math.floor(w / k), bh = Math.floor(h / k);
        if (bw >= 8 && bh >= 8) {
            const small = new Float32Array(bw * bh * 4), kk = k * k;
            for (let y = 0; y < bh; y++) {
                for (let x = 0; x < bw; x++) {
                    let r = 0, g = 0, b = 0, mx = 0;
                    for (let dy = 0; dy < k; dy++) {
                        let i = ((y * k + dy) * w + x * k) * 4;
                        for (let dx = 0; dx < k; dx++, i += 4) {
                            r += data[i]; g += data[i + 1]; b += data[i + 2];
                            const m = Math.max(data[i], data[i + 1], data[i + 2]); if (m > mx) mx = m;
                        }
                    }
                    const o = (y * bw + x) * 4;
                    small[o] = r / kk; small[o + 1] = g / kk; small[o + 2] = b / kk;
                    small[o + 3] = mx;   // a block touching a clipped pixel stays clipped
                }
            }
            const q = opts.quad ? opts.quad.map((p) => ({ x: p.x / k, y: p.y / k })) : null;
            return histograms(small, bw, bh, Object.assign({}, opts, { box: 0, quad: q, alphaIsMax: true }));
        }
    }
    const hr = new Uint32Array(HBINS), hg = new Uint32Array(HBINS), hb = new Uint32Array(HBINS), hl = new Uint32Array(HBINS);
    const step = Math.max(1, opts.step | 0 || 1);
    const alphaMax = !!opts.alphaIsMax;
    const clipAt = opts.clipAt || 256;
    const q = opts.quad || null;
    const top = HBINS - 1;
    const bin = (v) => { const k = (v * HS + 0.5) | 0; return k > top ? top : k < 0 ? 0 : k; };
    let x0 = 0, y0 = 0, x1 = w - 1, y1 = h - 1;
    if (q) {
        x0 = Math.max(0, Math.floor(Math.min(q[0].x, q[1].x, q[2].x, q[3].x)));
        x1 = Math.min(w - 1, Math.ceil(Math.max(q[0].x, q[1].x, q[2].x, q[3].x)));
        y0 = Math.max(0, Math.floor(Math.min(q[0].y, q[1].y, q[2].y, q[3].y)));
        y1 = Math.min(h - 1, Math.ceil(Math.max(q[0].y, q[1].y, q[2].y, q[3].y)));
    }
    let n = 0, clipped = 0, total = 0;
    for (let y = y0; y <= y1; y += step) {
        let i = (y * w + x0) * 4;
        for (let x = x0; x <= x1; x += step, i += 4 * step) {
            if (q && !inQuad(q, x + 0.5, y + 0.5)) continue;
            total++;
            const r = data[i], g = data[i + 1], b = data[i + 2];
            if (alphaMax ? data[i + 3] >= clipAt : (r >= clipAt || g >= clipAt || b >= clipAt)) { clipped++; continue; }
            hr[bin(r)]++; hg[bin(g)]++; hb[bin(b)]++; hl[bin((54 * r + 183 * g + 19 * b) / 256)]++;
            n++;
        }
    }
    return { r: hr, g: hg, b: hb, l: hl, n, clipped, total };
}

/**
 * Value (0..255, fractional) at quantile q of a histogram holding n samples.
 *
 * Two refinements over a plain quantile, both measured on the synthetic set:
 *  - interpolation inside a bin, so the live levels glide instead of stepping;
 *  - a quantile that lands on the slope of a flat area (a white wall, the bare
 *    base) snaps to that area's peak nearby: the tail of its NOISE is not its
 *    tone. Without the snap the white point sat on the noise tail of the
 *    whitest patch and every highlight came out ~5 levels dim.
 */
export function pct(hist, n, q) {
    const B = hist.length, S = B / 256;
    if (!n) return q < 0.5 ? 0 : 255;
    const target = clamp01(q) * (n - 1);
    let cum = 0;
    for (let i = 0; i < B; i++) {
        const c = hist[i];
        if (!c) continue;
        if (cum + c > target) {
            const R = Math.round(2 * S);
            let m = i;
            for (let k = Math.max(0, i - R); k <= Math.min(B - 1, i + R); k++) if (hist[k] > hist[m]) m = k;
            if (hist[m] > n * 0.004) {
                // centre of mass of the peak's three bins — sub-bin precision
                const a = m > 0 ? hist[m - 1] : 0, b = hist[m], d = m < B - 1 ? hist[m + 1] : 0;
                return Math.min(255, Math.max(0, (m + (d - a) / (a + b + d)) / S));
            }
            const v = (i + (target - cum + 0.5) / c - 0.5) / S;
            return v < 0 ? 0 : v > 255 ? 255 : v;
        }
        cum += c;
    }
    return 255;
}

/**
 * The frame's own tonal range: low and high quantiles of the RAW (negative)
 * values, per channel — or of luma, replicated, for B&W. What these mean
 * depends on the film: on a negative the high raw value is the thinnest film,
 * i.e. the picture's black.
 */
export function computeLevels(h, mode, clip = AUTO_CLIP) {
    const lo = [0, 0, 0], hi = [0, 0, 0];
    if (mode === 'bw') {
        const a = pct(h.l, h.n, clip.lo), b = pct(h.l, h.n, clip.hi);
        for (let c = 0; c < 3; c++) { lo[c] = a; hi[c] = b; }
    } else {
        const hs = [h.r, h.g, h.b];
        for (let c = 0; c < 3; c++) { lo[c] = pct(hs[c], h.n, clip.lo); hi[c] = pct(hs[c], h.n, clip.hi); }
    }
    // A flat frame (a blank bit of film, a lens cap) must not divide by zero
    // or explode a hair of noise into full contrast.
    for (let c = 0; c < 3; c++) {
        if (hi[c] - lo[c] < 6) { const m = (hi[c] + lo[c]) / 2; lo[c] = Math.max(0, m - 3); hi[c] = Math.min(255, m + 3); }
    }
    return { lo, hi };
}

/** Linear gains for temperature (blue↔amber) and tint (green↔magenta),
 *  normalised so a grey keeps its luminance. */
export function tempTintGains(temp, tint) {
    const gr = Math.pow(2, 0.42 * (temp || 0));
    const gb = Math.pow(2, -0.42 * (temp || 0));
    const gg = Math.pow(2, -0.32 * (tint || 0));
    const k = 0.2126 * gr + 0.7152 * gg + 0.0722 * gb;
    return [gr / k, gg / k, gb / k];
}

/** Default adjustments — "neutral" means the maths above, nothing added. */
export function defaultAdjust() {
    return {
        exposure: 0,     // EV
        bright: 0,       // midtones, -1..1
        contrast: 0,     // -1..1
        temp: 0,         // -1..1
        tint: 0,         // -1..1
        sat: 1,          // 0..2
        neutral: null,   // [gr, gg, gb] from "tap a grey"
        lv: { m: { b: 0, m: 0, w: 1 }, r: { b: 0, m: 0, w: 1 }, g: { b: 0, m: 0, w: 1 }, b: { b: 0, m: 0, w: 1 } },
    };
}

const CH = ['r', 'g', 'b'];

/**
 * The per-channel transfer, before the viewer's adjustments: raw 8-bit value
 * → linear positive (0 = black point, 1 = white point, may overshoot).
 *
 *   P.mode   'color' | 'bw' | 'slide'
 *   P.gamma  film gamma (defaults per mode)
 *   P.levels {lo:[3], hi:[3]} raw quantiles from computeLevels()
 *   P.wb     'auto' — black and white point per channel (full auto levels);
 *            'base' — the sampled film base is black for every channel at once
 *                     (neutral shadows by physics), white per channel;
 *            'off'  — slide only: no correction at all.
 *   P.base   [r, g, b] raw values of the bare film base (for 'base').
 */
export function makeNormalizer(P) {
    const mode = P.mode || 'color';
    const neg = mode !== 'slide';
    const inv = 1 / (P.gamma || FILM_GAMMA[mode] || 0.6);
    const L = P.levels || { lo: [0, 0, 0], hi: [255, 255, 255] };
    const nCh = mode === 'bw' ? 1 : 3;
    const lin = (v) => Math.max(dec(v), 1e-5);

    if (!neg) {
        if (P.wb === 'off') return (c, v) => dec(v);
        const b = [], wd = [];
        for (let c = 0; c < nCh; c++) { b[c] = dec(L.lo[c]); wd[c] = Math.max(1e-5, dec(L.hi[c]) - b[c]); }
        return (c, v) => (dec(v) - b[c]) / wd[c];
    }

    // Negative: p = (ref / t)^(1/γ), then black/white mapping in p.
    const useBase = P.wb === 'base' && P.base;
    const ref = [], pb = [], pw = [];
    for (let c = 0; c < nCh; c++) {
        const blackV = L.hi[c], whiteV = L.lo[c];
        ref[c] = useBase ? lin(mode === 'bw' ? luma8(P.base[0], P.base[1], P.base[2]) : P.base[c]) : lin(blackV);
        pb[c] = Math.pow(ref[c] / lin(blackV), inv);
        pw[c] = Math.pow(ref[c] / lin(whiteV), inv);
    }
    if (useBase) {
        // One black for all channels: at the base the three dyes are equally
        // absent, so a neutral black is a physical fact, not a guess. Never
        // below the base itself — nothing on the film is thinner than it.
        // And one white: once the base has divided out the lamp, the camera's
        // white balance and the orange mask, what is left is the scene as the
        // film recorded it. Stretching each channel's white separately (what
        // «Auto» does, like a lab printer's per-frame balance) would repaint a
        // deliberately warm sunset or a teal-graded street neutral — measured
        // on the e2e photo: per-channel whites moved the mean G−R by 10 levels.
        let k = Infinity, kw = 0;
        for (let c = 0; c < nCh; c++) { k = Math.min(k, pb[c]); kw = Math.max(kw, pw[c]); }
        k = Math.max(1, k);
        for (let c = 0; c < nCh; c++) { pb[c] = k; pw[c] = kw; }
    }
    for (let c = 0; c < nCh; c++) if (pw[c] - pb[c] < 1e-3) pw[c] = pb[c] + 1e-3;
    return (c, v) => (Math.pow(ref[c] / lin(v), inv) - pb[c]) / (pw[c] - pb[c]);
}

function applyLv(n, s) {
    if (!s) return n;
    const w = Math.max(s.b + 0.01, s.w);
    n = (n - s.b) / (w - s.b);
    if (n > 0 && s.m) n = Math.pow(n, Math.pow(2, -s.m));
    return n;
}

/** Smoothstep S-curve blend: c > 0 adds contrast, c < 0 flattens. Stays in 0..1. */
export function contrastCurve(y, c) {
    if (!c) return y;
    const s = y * y * (3 - 2 * y);
    return clamp01(y + c * (s - y));
}

/**
 * Everything after the normaliser, for one channel value: returns the linear
 * value BEFORE encoding (for "tap a grey", which needs to see it).
 */
export function makeLinearEval(P) {
    const A = Object.assign(defaultAdjust(), P.adjust || {});
    const norm = makeNormalizer(P);
    const bw = P.mode === 'bw';
    const tt = bw ? [1, 1, 1] : tempTintGains(A.temp, A.tint);
    const ng = (!bw && A.neutral) ? A.neutral : [1, 1, 1];
    const ex = Math.pow(2, A.exposure || 0);
    const lv = A.lv || {};
    return (c, v) => {
        let n = norm(bw ? 0 : c, v);
        if (!bw) n = applyLv(n, lv[CH[c]]);
        n = applyLv(n, lv.m);
        return n * ex * tt[c] * ng[c];
    };
}

/**
 * The lookup table: Uint8Array(1024), RGBA-interleaved (256 texels × 4), so it
 * uploads to WebGL unchanged. For B&W the three channels are equal and the
 * table is indexed by luma.
 */
export function buildLut(P) {
    const A = Object.assign(defaultAdjust(), P.adjust || {});
    const f = makeLinearEval(P);
    const be = Math.pow(2, -(A.bright || 0));
    const bw = P.mode === 'bw';
    const lut = new Uint8Array(1024);
    for (let c = 0; c < 3; c++) {
        for (let v = 0; v < 256; v++) {
            let n = f(bw ? 0 : c, v);
            n = n > 0 ? Math.pow(Math.min(n, 1), be) : 0;
            const y = contrastCurve(enc(n), A.contrast || 0);
            lut[v * 4 + c] = Math.round(y * 255);
        }
    }
    for (let v = 0; v < 256; v++) lut[v * 4 + 3] = 255;
    if (bw) for (let v = 0; v < 256; v++) { lut[v * 4 + 1] = lut[v * 4]; lut[v * 4 + 2] = lut[v * 4]; }
    return lut;
}

/**
 * Apply a table to an RGBA buffer (in place when dst === src). `sat` mixes the
 * result towards its own luma, in encoded values — the shader does the same.
 */
export function applyLut(src, dst, lut, bw, sat = 1) {
    const n = src.length;
    const s = sat === 1 ? null : sat;
    for (let i = 0; i < n; i += 4) {
        if (bw) {
            const v = lut[luma8(src[i], src[i + 1], src[i + 2]) * 4];
            dst[i] = v; dst[i + 1] = v; dst[i + 2] = v; dst[i + 3] = 255;
            continue;
        }
        let r = lut[src[i] * 4], g = lut[src[i + 1] * 4 + 1], b = lut[src[i + 2] * 4 + 2];
        if (s !== null) {
            const y = 0.2126 * r + 0.7152 * g + 0.0722 * b;
            r = y + (r - y) * s; g = y + (g - y) * s; b = y + (b - y) * s;
            r = r < 0 ? 0 : r > 255 ? 255 : r;
            g = g < 0 ? 0 : g > 255 ? 255 : g;
            b = b < 0 ? 0 : b > 255 ? 255 : b;
        }
        dst[i] = r; dst[i + 1] = g; dst[i + 2] = b; dst[i + 3] = 255;
    }
    return dst;
}

/**
 * "Tap on something grey": gains that make the raw pixel `rgb` neutral while
 * keeping its luminance. Temperature and tint are ignored while measuring —
 * the tap replaces them.
 */
export function neutralGains(P, rgb) {
    const probe = Object.assign({}, P, { adjust: Object.assign(defaultAdjust(), P.adjust || {}, { temp: 0, tint: 0, neutral: null, exposure: 0 }) });
    const f = makeLinearEval(probe);
    const n = [0, 1, 2].map((c) => Math.max(1e-4, f(c, rgb[c])));
    const y = 0.2126 * n[0] + 0.7152 * n[1] + 0.0722 * n[2];
    return n.map((v) => Math.min(4, Math.max(0.25, y / v)));
}

/** Median of a small window around (x, y) — what a tap samples, so a single
 *  noisy pixel or a speck of dust does not decide the film base. */
export function sampleAt(data, w, h, x, y, r = 2) {
    const vals = [[], [], []];
    for (let yy = Math.max(0, Math.round(y) - r); yy <= Math.min(h - 1, Math.round(y) + r); yy++) {
        for (let xx = Math.max(0, Math.round(x) - r); xx <= Math.min(w - 1, Math.round(x) + r); xx++) {
            const i = (yy * w + xx) * 4;
            vals[0].push(data[i]); vals[1].push(data[i + 1]); vals[2].push(data[i + 2]);
        }
    }
    return vals.map((a) => { a.sort((p, q) => p - q); return a.length ? a[a.length >> 1] : 0; });
}

/** Film type from a frame's colours, for the preselection hint only: an
 *  orange-masked negative has blue far below red across the whole frame. */
export function guessFilm(h) {
    if (!h.n) return null;
    const mr = pct(h.r, h.n, 0.5), mg = pct(h.g, h.n, 0.5), mb = pct(h.b, h.n, 0.5);
    const spread = Math.max(mr, mg, mb) - Math.min(mr, mg, mb);
    if (mr > mb + 30 && mr >= mg) return 'color';
    if (spread < 10) return 'bw';
    return null;
}
